import { ContentCapacitySchema, CreateContentsResponseSchema, DeleteContentMaterialResponseSchema,
  TaskMaterialListSchema,
  TaskMaterialWriteResponseSchema, TaskWriteResponseSchema, VideoResultStageReceiptSchema, VoidContentResponseSchema,
  type AddTaskFileMaterialRequest, type AddTaskLinkMaterialRequest, type AssignTaskV2Request,
  type ContentCapacity, type CreateContentItem, type CreateContentsResponse, type DeleteContentMaterialRequest,
  type DeleteContentMaterialResponse,
  type PublishTaskMarkdownVersionRequest, type ReturnSubmittedTaskRequest, type SaveTaskMarkdownDraftRequest,
  type TaskMaterialList, type TaskMaterialWriteResponse, type TaskWriteResponse, type VideoResultStageReceipt,
  type TransitionTaskV2Request, type VoidContentRequest, type VoidContentResponse } from '@youkuai/contracts';

import { apiDownload, apiRequest } from './project-api.ts';

function packageBase(projectId: string, packageId: string): string {
  return `/projects/${encodeURIComponent(projectId)}/service-packages/${encodeURIComponent(packageId)}`;
}

function taskBase(projectId: string, taskId: string): string {
  return `/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}`;
}

function json(key: string, body: unknown, method = 'POST'): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key }, body: JSON.stringify(body) };
}

export function getContentCapacity(token: string, projectId: string, packageId: string): Promise<ContentCapacity> {
  return apiRequest(`${packageBase(projectId, packageId)}/capacity`, token).then((value) => ContentCapacitySchema.parse(value));
}

export function createContents(input: { token: string; projectId: string; packageId: string; key: string;
  items: CreateContentItem[] }): Promise<CreateContentsResponse> {
  const batch = input.items.length !== 1;
  return apiRequest(`${packageBase(input.projectId, input.packageId)}/contents${batch ? '/batch' : ''}`,
    input.token, json(input.key, batch ? { items: input.items } : input.items[0])).then((value) =>
      CreateContentsResponseSchema.parse(value));
}

export function voidContent(input: { token: string; projectId: string; packageId: string; contentId: string;
  key: string; change: VoidContentRequest }): Promise<VoidContentResponse> {
  return apiRequest(`${packageBase(input.projectId, input.packageId)}/contents/${encodeURIComponent(input.contentId)}/void`,
    input.token, json(input.key, input.change)).then((value) => VoidContentResponseSchema.parse(value));
}

export function assignTaskV2(input: { token: string; projectId: string; taskId: string; key: string;
  assignment: AssignTaskV2Request; signal?: AbortSignal | undefined }): Promise<TaskWriteResponse> {
  const options = json(input.key, input.assignment);
  if (input.signal) options.signal = input.signal;
  return apiRequest(`${taskBase(input.projectId, input.taskId)}/assign`, input.token, options)
    .then((value) => TaskWriteResponseSchema.parse(value));
}

export function transitionTaskV2(input: { token: string; projectId: string; taskId: string; key: string;
  action: 'complete' | 'reopen' | 'return';
  change: TransitionTaskV2Request | ReturnSubmittedTaskRequest }): Promise<TaskWriteResponse> {
  return apiRequest(`${taskBase(input.projectId, input.taskId)}/${input.action}`, input.token,
    json(input.key, input.change)).then((value) => TaskWriteResponseSchema.parse(value));
}

export function listTaskMaterials(token: string, projectId: string, taskId: string): Promise<TaskMaterialList> {
  return apiRequest(`${taskBase(projectId, taskId)}/materials`, token).then((value) => TaskMaterialListSchema.parse(value));
}

export function listContentMaterials(token: string, projectId: string, contentId: string): Promise<TaskMaterialList> {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/contents/${encodeURIComponent(contentId)}/materials`, token)
    .then((value) => TaskMaterialListSchema.parse(value));
}

export function deleteContentMaterial(input: { token: string; projectId: string; contentId: string;
  materialId: string; key: string; change: DeleteContentMaterialRequest }): Promise<DeleteContentMaterialResponse> {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/contents/${encodeURIComponent(input.contentId)}` +
    `/materials/${encodeURIComponent(input.materialId)}/delete`, input.token, json(input.key, input.change))
    .then((value) => DeleteContentMaterialResponseSchema.parse(value));
}

export function downloadContentMaterial(token: string, path: string): Promise<Blob> {
  return apiDownload(path, token);
}

export function downloadTaskMaterial(token: string, projectId: string, taskId: string,
  materialId: string): Promise<Blob> {
  return apiDownload(`${taskBase(projectId, taskId)}/materials/${encodeURIComponent(materialId)}/download`, token);
}

export function stageTaskMaterial(input: { token: string; projectId: string; packageId: string; contentId: string;
  taskId: string; contentType: 'VIDEO' | 'ARTICLE'; expectedVersion: number; key: string; file: File }):
  Promise<VideoResultStageReceipt> {
  const query = new URLSearchParams({ filename: input.file.name, expectedVersion: String(input.expectedVersion),
    contentType: input.contentType });
  const path = `${packageBase(input.projectId, input.packageId)}/contents/${encodeURIComponent(input.contentId)}` +
    `/tasks/${encodeURIComponent(input.taskId)}/material-uploads?${query}`;
  return apiRequest(path, input.token, { method: 'POST', headers: {
    'Content-Type': input.file.type || 'application/octet-stream', 'Idempotency-Key': input.key },
  body: input.file }).then((value) => VideoResultStageReceiptSchema.parse(value));
}

export function addFileMaterial(input: { token: string; projectId: string; taskId: string; key: string;
  material: AddTaskFileMaterialRequest }): Promise<TaskMaterialWriteResponse> {
  return apiRequest(`${taskBase(input.projectId, input.taskId)}/file-materials`, input.token,
    json(input.key, input.material)).then((value) => TaskMaterialWriteResponseSchema.parse(value));
}

export function addLinkMaterial(input: { token: string; projectId: string; taskId: string; key: string;
  material: AddTaskLinkMaterialRequest }): Promise<TaskMaterialWriteResponse> {
  return apiRequest(`${taskBase(input.projectId, input.taskId)}/link-materials`, input.token,
    json(input.key, input.material)).then((value) => TaskMaterialWriteResponseSchema.parse(value));
}

export function saveMarkdownDraft(input: { token: string; projectId: string; taskId: string; key: string;
  material: SaveTaskMarkdownDraftRequest }): Promise<TaskMaterialWriteResponse> {
  return apiRequest(`${taskBase(input.projectId, input.taskId)}/markdown-draft`, input.token,
    json(input.key, input.material, 'PUT')).then((value) => TaskMaterialWriteResponseSchema.parse(value));
}

export function publishMarkdownVersion(input: { token: string; projectId: string; taskId: string; key: string;
  material: PublishTaskMarkdownVersionRequest }): Promise<TaskMaterialWriteResponse> {
  return apiRequest(`${taskBase(input.projectId, input.taskId)}/markdown-versions`, input.token,
    json(input.key, input.material)).then((value) => TaskMaterialWriteResponseSchema.parse(value));
}
